import { useState, useCallback, useRef, useEffect } from 'react';
import { useVoiceLinkV2, type PerformanceMetrics } from './useVoiceLinkV2';

export type { PerformanceMetrics };

export interface Message {
  role: 'user' | 'assistant';
  content: string;
}

interface UseSpeakingChatbotV2Options {
  projectId?: string;
  entityType: string;
  entityId: string;
  entityName: string;
  fieldConfig: Record<string, string>;
  fieldLabels: Record<string, { label: string; description: string }>;  kbId?: string;
  kbName?: string;
  projectName: string;
  documentIds?: string[];
  language?: 'en' | 'fr';
  activeTab: string;
  activeField: string | null;
  onFieldUpdate: (fieldName: string, value: string) => void;
  getFieldValue: (fieldName: string) => string;
  onContextFieldChanged?: (targetField: string) => void;
  onExtractionResults?: (data: unknown) => void;
  onPilotHandoff?: () => void;
  onDraftUpdated?: () => void;
  onFtoUpdated?: () => void;
  onError?: (error: string) => void;
  /** Called when the agent emits a switch_tab event — payload is the target tab ID. */
  onSwitchTab?: (tab: string) => void;
  solutionId?: string;
  proactiveInit?: boolean;
}

export const useSpeakingChatbotV2 = (options: UseSpeakingChatbotV2Options) => {
  const {
    projectId,
    entityType,
    entityId,
    entityName,
    fieldConfig,
    fieldLabels,
    kbId,
    kbName,
    projectName,
    documentIds = [],
    language = 'fr',
    activeTab,
    activeField,
    onFieldUpdate,
    getFieldValue,
    onContextFieldChanged,
    onExtractionResults,
    onPilotHandoff,
    onDraftUpdated,
    onFtoUpdated,
    onError,
    onSwitchTab,
    solutionId,
    proactiveInit = false,
  } = options;

  const [messages, setMessages] = useState<Message[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [partialTranscript, setPartialTranscript] = useState('');
  const [metrics, setMetrics] = useState<PerformanceMetrics | null>(null);
  const [statusMessage, setStatusMessage] = useState<string>('');
  const [isConversationMode, setIsConversationMode] = useState(false);
  const conversationModeRef = useRef(false);
  const isRecordingRef = useRef(false);
  const prevIsPlayingRef = useRef(false);
  const assistantMessageRef = useRef('');
  const isReadyRef = useRef(false);
  const processingTimeoutRef = useRef<number | null>(null);
  const previousDocumentIdsRef = useRef<string[]>([]);

  // DEBUG: Log projectId only when it changes (not on every render)
  useEffect(() => {
    console.log('[useSpeakingChatbotV2] 🔍 Received projectId:', projectId);
  }, [projectId]);

  // Stable callbacks using refs to prevent WebSocket reconnection
  const onFieldUpdateRef = useRef(onFieldUpdate);
  const getFieldValueRef = useRef(getFieldValue);
  const onContextFieldChangedRef = useRef(onContextFieldChanged);
  const onExtractionResultsRef = useRef(onExtractionResults);
  const onPilotHandoffRef = useRef(onPilotHandoff);
  const onDraftUpdatedRef = useRef(onDraftUpdated);
  const onFtoUpdatedRef = useRef(onFtoUpdated);
  const onErrorRef = useRef(onError);
  const onSwitchTabRef = useRef(onSwitchTab);

  useEffect(() => {
    onFieldUpdateRef.current = onFieldUpdate;
    getFieldValueRef.current = getFieldValue;
    onContextFieldChangedRef.current = onContextFieldChanged;
    onExtractionResultsRef.current = onExtractionResults;
    onPilotHandoffRef.current = onPilotHandoff;
    onDraftUpdatedRef.current = onDraftUpdated;
    onFtoUpdatedRef.current = onFtoUpdated;
    onErrorRef.current = onError;
    onSwitchTabRef.current = onSwitchTab;
  }, [onFieldUpdate, getFieldValue, onContextFieldChanged, onExtractionResults, onPilotHandoff, onDraftUpdated, onFtoUpdated, onError, onSwitchTab]);

  // Handle final transcript
  const handleTranscript = useCallback((text: string, isFinal: boolean) => {
    if (isFinal) {
      console.log('[SpeakingChatbotV2] ⭐ FINAL transcript received:', text);
      setPartialTranscript('');
      setMessages(prev => [...prev, { role: 'user', content: text }]);
      setIsProcessing(true);
      setStatusMessage(''); // Clear any previous status
      assistantMessageRef.current = '';
      
      // Safety timeout: If no response after 300s, clear processing state
      if (processingTimeoutRef.current) {
        clearTimeout(processingTimeoutRef.current);
      }
      processingTimeoutRef.current = setTimeout(() => {
        console.error('[SpeakingChatbotV2] No response received after 300s, clearing processing state');
        setIsProcessing(false);
        setStatusMessage('');
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: 'Request timed out. This may happen when reading large documents. Please try again.' 
        }]);
      }, 300000);
    } else {
      setPartialTranscript(text);
    }
  }, []);

  // Handle LLM tokens
  const handleToken = useCallback((token: string) => {
    console.log('[SpeakingChatbotV2] 🔤 Token received:', token);
    
    // Clear timeout and status when we start receiving tokens
    if (processingTimeoutRef.current) {
      clearTimeout(processingTimeoutRef.current);
      processingTimeoutRef.current = null;
    }
    setStatusMessage(''); // Clear status when response starts
    
    assistantMessageRef.current += token;
    setMessages(prev => {
      const newMsgs = [...prev];
      if (newMsgs.length > 0 && newMsgs[newMsgs.length - 1].role === 'assistant') {
        newMsgs[newMsgs.length - 1].content = assistantMessageRef.current;
      } else {
        newMsgs.push({ role: 'assistant', content: assistantMessageRef.current });
      }
      return newMsgs;
    });
    setIsProcessing(false); // Clear processing state when we start receiving tokens
  }, []);

  // Handle response complete
  const handleResponseComplete = useCallback((content: string) => {
    console.log('[SpeakingChatbotV2] ✅ Response complete');
    
    // Clear timeout and status
    if (processingTimeoutRef.current) {
      clearTimeout(processingTimeoutRef.current);
      processingTimeoutRef.current = null;
    }
    setStatusMessage(''); // Clear status
    
    setMessages(prev => {
      const newMsgs = [...prev];
      if (newMsgs.length > 0 && newMsgs[newMsgs.length - 1].role === 'assistant') {
        newMsgs[newMsgs.length - 1].content = content;
      }
      return newMsgs;
    });
    assistantMessageRef.current = '';
    setIsProcessing(false);
  }, []);

  // Handle field_updated event from AI
  const handleFieldUpdated = useCallback((fieldName: string, value: unknown) => {
    console.log('[SpeakingChatbotV2] 📝 Field updated by AI:', fieldName);
    onFieldUpdateRef.current(fieldName, value as string);
  }, []);

  // Handle field_read request - return value to backend
  const handleFieldRead = useCallback((fieldName: string) => {
    const value = getFieldValueRef.current(fieldName);
    console.log('[SpeakingChatbotV2] 📖 Field read:', fieldName, '=', value.substring(0, 50));
    // Send value back to backend via sendFieldValue (will be called after hook init)
    return value;
  }, []);

  // Handle errors
  const handleError = useCallback((err: string) => {
    console.error('[SpeakingChatbotV2] Error:', err);
    onErrorRef.current?.(err);
    setIsProcessing(false);

    // TTS errors must NOT be pushed as chat bubbles.
    // The backend always sends response_complete after _stream_tts(), so if we
    // pushed a TTS error bubble here, response_complete would find 'assistant'
    // as the last role and overwrite it with the LLM text — creating a duplicate
    // bubble (one from handleToken, one overwritten from the error slot).
    // Surface TTS errors as a transient status bar message instead.
    if (err.startsWith('TTS error:')) {
      setStatusMessage('Audio unavailable');
      return;
    }

    setStatusMessage(''); // Clear status on other errors
    
    // Only show connection errors in chat if user has already started conversation
    // (Avoid cluttering chat with auto-connect failures)
    const isConnectionError = err.includes('Connection') || err.includes('WebSocket');
    const hasUserMessages = messages.some(m => m.role === 'user');
    
    if (!isConnectionError || hasUserMessages) {
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: `Error: ${err}` 
      }]);
    } else {
      console.log('[SpeakingChatbotV2] Suppressing connection error from chat (no user activity yet)');
    }
  }, [messages]);

  // Handle workspace actions (e.g., document reading status)
  const handleWorkspaceAction = useCallback((action: string, data?: unknown) => {
    console.log('[SpeakingChatbotV2] 🔧 Workspace action:', action, data);
    const d = data as { query?: string; target_field?: string; same_scope?: boolean; intent?: string; reason?: string; field?: string } | undefined;
    switch (action) {
      case 'reading_documents':
        setStatusMessage('Reading selected documents...');
        break;
      case 'search_started':
        setStatusMessage(`Searching documents: ${d?.query || ''}`);
        break;
      case 'context_field_changed': {
        const targetField = d?.target_field;
        const sameScope = d?.same_scope === true;
        console.log('[SpeakingChatbotV2] CFA switched to:', targetField, 'same_scope:', sameScope);
        // Only clear conversation history on cross-scope switches
        if (!sameScope) {
          setMessages([]);
          setStatusMessage('');
          if (processingTimeoutRef.current) {
            clearTimeout(processingTimeoutRef.current);
            processingTimeoutRef.current = null;
          }
          assistantMessageRef.current = '';
          setIsProcessing(false);
        }
        // Notify parent page to update activeField state and localStorage
        if (targetField) {
          onContextFieldChangedRef.current?.(targetField);
        }
        break;
      }
      case 'tool_executing': {
        setStatusMessage('Processing...');
        if (processingTimeoutRef.current) {
          clearTimeout(processingTimeoutRef.current);
        }
        processingTimeoutRef.current = setTimeout(() => {
          setIsProcessing(false);
          setStatusMessage('');
          setMessages(prev => [...prev, { role: 'assistant', content: 'Request timed out. Please try again.' }]);
        }, 300000);
        break;
      }
      case 'extraction_started':
        setStatusMessage(`Extracting documents: ${d?.intent || ''}`);
        break;
      case 'extraction_complete':
        setStatusMessage('');
        break;
      case 'extraction_results':
        setStatusMessage('');
        onExtractionResultsRef.current?.(data);
        break;
      case 'switch_tab':
        onSwitchTabRef.current?.((data as { tab?: string })?.tab || '');
        break;
      case 'pilot_handoff_requested':
        console.log('[SpeakingChatbotV2] Pilot handoff requested:', d?.reason);
        onPilotHandoffRef.current?.();
        break;
      case 'draft_updated':
      case 'draft_published':
        console.log('[SpeakingChatbotV2] Draft event:', action, d?.field);
        onDraftUpdatedRef.current?.();
        break;
      case 'scoping_drafted':
        console.log('[SpeakingChatbotV2] SOTA scoping note drafted');
        onDraftUpdatedRef.current?.();
        break;
      case 'coherence_pass_complete':
        console.log('[SpeakingChatbotV2] SOTA coherence pass complete, flags:', (d as Record<string, unknown>)?.flag_count);
        onDraftUpdatedRef.current?.();
        break;
      case 'fto_scope_updated':
        onFtoUpdatedRef.current?.();
        break;
      default:
        console.log('[SpeakingChatbotV2] Unhandled workspace action:', action);
    }
  }, []);

  // Handle performance metrics
  const handleMetrics = useCallback((metricsData: PerformanceMetrics) => {
    console.log('[SpeakingChatbotV2] 📊 Performance metrics:', metricsData);
    setMetrics(metricsData);
  }, []);

  const {
    isReady,
    isConnecting,
    isRecording,
    isPlaying,
    connect,
    disconnect: _disconnect, // Unused but kept for future use
    startRecording,
    stopRecording,
    sendTextMessage: sendText,
    sendActiveState,
    sendFieldValue: _sendFieldValue, // Unused but kept for future use
    updateDocumentIds,
    availableDevices,
    selectedDeviceId,
    setSelectedDeviceId,
    audioLevel,
  } = useVoiceLinkV2({
    projectId,
    entityType,
    entityId,
    entityName,
    fieldConfig,
    fieldLabels,
    kbId,
    kbName,
    projectName,
    documentIds,
    language,
    solutionId,
    activeField: activeField ?? undefined,
    onTranscript: handleTranscript,
    onToken: handleToken,
    onResponseComplete: handleResponseComplete,
    onFieldUpdated: handleFieldUpdated,
    onFieldRead: handleFieldRead,
    onMetrics: handleMetrics,
    onError: handleError,
    onAction: handleWorkspaceAction, // Handle workspace actions for status updates
  });

  // Proactive init: fire a silent __init__ signal when connection is first ready
  // The __init__ message is sent to the WebSocket but not added to the messages UI list.
  // The LLM responds with a structured opening (greeting + first brainstorming question).
  const proactiveInitSentRef = useRef(false);
  useEffect(() => {
    if (!proactiveInit || !isReady || proactiveInitSentRef.current) return;
    proactiveInitSentRef.current = true;
    // Delay 200 ms to let the connection stabilise before the first LLM call
    const timer = setTimeout(() => {
      if (isReadyRef.current) {
        sendText('__init__');
        setIsProcessing(true);
        assistantMessageRef.current = '';
      }
    }, 200);
    return () => clearTimeout(timer);
  }, [isReady, proactiveInit, sendText]);

  // Keep isReadyRef in sync with isReady
  useEffect(() => {
    console.log('[SpeakingChatbotV2] isReady changed to:', isReady);
    isReadyRef.current = isReady;
    
    // Clear error message if connection becomes ready
    if (isReady && messages.length > 0) {
      const lastMsg = messages[messages.length - 1];
      if (lastMsg.role === 'assistant' && lastMsg.content.includes('Connection lost')) {
        // Remove stale connection error message
        setMessages(prev => prev.slice(0, -1));
      }
    }
  }, [isReady, messages]);

  // Update backend when document selection changes (after connection is ready)
  // Only send if IDs actually changed (avoid redundant updates on re-renders)
  useEffect(() => {
    console.log('[SpeakingChatbotV2] Document update check:', {
      isReady,
      hasUpdateFunction: !!updateDocumentIds,
      documentIdsCount: documentIds.length,
      documentIds
    });
    
    if (!isReady || !updateDocumentIds) {
      console.log('[SpeakingChatbotV2] ⚠️ Skipping document update - not ready or no update function');
      return;
    }
    
    // Check if documentIds actually changed (deep comparison)
    const prev = previousDocumentIdsRef.current;
    const current = documentIds;
    
    const hasChanged = 
      prev.length !== current.length || 
      prev.some((id, idx) => id !== current[idx]);
    
    if (hasChanged) {
      console.log('[SpeakingChatbotV2] 📄 Document selection changed, updating backend:', {
        previous: prev,
        current: current
      });
      updateDocumentIds(current);
      previousDocumentIdsRef.current = [...current]; // Store copy
    } else {
      console.log('[SpeakingChatbotV2] 📋 Document IDs unchanged, skipping update:', current);
    }
  }, [documentIds, isReady, updateDocumentIds]);

  // Auto-connect WebSocket on mount (so text messages work without clicking microphone)
  // GUARD: Only connect when essential data is ready
  useEffect(() => {
    if (!entityType || !entityId) {
      console.log('[SpeakingChatbotV2] ⏸️ Skipping auto-connect - entity data not ready yet');
      return;
    }
    
    console.log('[SpeakingChatbotV2] Auto-connecting WebSocket on mount...');
    console.log('[SpeakingChatbotV2] Entity:', { entityType, entityId, projectId, kbId });
    connect();
    // Cleanup: disconnect on unmount
    return () => {
      console.log('[SpeakingChatbotV2] Component unmounting, connection will be cleaned up by useVoiceLinkV2');
      // Note: Don't call disconnect() here as it's already handled by useVoiceLinkV2's cleanup
    };
  }, [connect, entityType, entityId, projectId, kbId]);

  // Send active state updates when tab or field changes
  useEffect(() => {
    if (isReady) {
      sendActiveState(activeTab, activeField);
    }
  }, [activeTab, activeField, isReady, sendActiveState]);

  // Keep isRecordingRef in sync to avoid stale closures in the auto-return effect
  useEffect(() => { isRecordingRef.current = isRecording; }, [isRecording]);

  // Auto-return to listen: when TTS playback ends, re-arm the mic if in conversation mode
  useEffect(() => {
    if (prevIsPlayingRef.current && !isPlaying && conversationModeRef.current && !isRecordingRef.current) {
      console.log('[SpeakingChatbotV2] Auto-returning to listen mode');
      const id = setTimeout(async () => {
        if (conversationModeRef.current && !isRecordingRef.current) {
          await startRecording();
        }
      }, 150);
      prevIsPlayingRef.current = isPlaying;
      return () => clearTimeout(id);
    }
    prevIsPlayingRef.current = isPlaying;
  }, [isPlaying, startRecording]);

  // Watchdog: recover from "Ready to listen" stuck state (conversation mode active, connected,
  // but not recording / processing / playing — e.g. when TTS never started or failed silently)
  useEffect(() => {
    if (!isConversationMode || !isReady || isRecording || isProcessing || isPlaying) return;
    const id = setTimeout(async () => {
      if (conversationModeRef.current && isReadyRef.current && !isRecordingRef.current) {
        console.log('[SpeakingChatbotV2] Watchdog: restarting recording from stuck "Ready to listen" state');
        await startRecording();
      }
    }, 1500);
    return () => clearTimeout(id);
  }, [isConversationMode, isReady, isRecording, isProcessing, isPlaying, startRecording]);

  // Start chat (connect + record)
  const startChat = useCallback(async () => {
    conversationModeRef.current = true;
    setIsConversationMode(true);
    console.log('[SpeakingChatbotV2] startChat called, isReady:', isReady, 'isRecording:', isRecording);
    
    // If not connected, connect first and wait for ready state
    if (!isReady) {
      console.log('[SpeakingChatbotV2] Connecting...');
      connect();
      
      // Wait for connection to be ready (max 8 seconds - needs time for Gladia init)
      console.log('[SpeakingChatbotV2] Waiting for connection...');
      for (let i = 0; i < 80; i++) {
        await new Promise(resolve => setTimeout(resolve, 100));
        if (isReadyRef.current) {
          console.log('[SpeakingChatbotV2] ✅ Connection ready after', i * 100, 'ms');
          break;
        }
      }
      
      if (!isReadyRef.current) {
        console.error('[SpeakingChatbotV2] ❌ Connection timeout after 8s');
        onError?.('Failed to establish connection. Please try again.');
        return;
      }
    }
    
    // Start recording (this will reinitialize audio even if previously stopped)
    if (!isRecording) {
      console.log('[SpeakingChatbotV2] Starting recording...');
      await startRecording();
    } else {
      console.log('[SpeakingChatbotV2] Already recording');
    }
  }, [isReady, isRecording, connect, startRecording, onError]);

  // Commit speech (stop recording, trigger STT) — stays in conversation mode
  const commitSpeech = useCallback(() => {
    stopRecording();
  }, [stopRecording]);

  // Exit conversation mode entirely
  const stopChat = useCallback(() => {
    conversationModeRef.current = false;
    setIsConversationMode(false);
    stopRecording();
  }, [stopRecording]);

  // Send text message
  const sendTextMessage = useCallback(async (text: string) => {
    console.log('[SpeakingChatbotV2] sendTextMessage called, isReady:', isReadyRef.current);
    
    // Ensure connection is ready (should already be connected via auto-connect)
    if (!isReadyRef.current) {
      console.log('[SpeakingChatbotV2] Not ready, waiting for connection...');
      // Wait for connection to be ready (max 3 seconds - should be fast since auto-connect already started)
      for (let i = 0; i < 30; i++) {
        await new Promise(resolve => setTimeout(resolve, 100));
        if (isReadyRef.current) {
          console.log('[SpeakingChatbotV2] Connection ready after', i * 100, 'ms');
          break;
        }
      }
      if (!isReadyRef.current) {
        console.error('[SpeakingChatbotV2] Connection not ready after 3s');
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: 'Connection not established. Please wait a moment and try again, or click the microphone button to reconnect.' 
        }]);
        setIsProcessing(false);
        return;
      }
    }
    
    console.log('[SpeakingChatbotV2] Sending text message:', text);
    console.log('[SpeakingChatbotV2] About to call sendText function');
    setMessages(prev => [...prev, { role: 'user', content: text }]);
    setIsProcessing(true);
    setStatusMessage(''); // Clear any previous status
    assistantMessageRef.current = '';
    
    // Set timeout to clear processing state (300 seconds)
    if (processingTimeoutRef.current) {
      clearTimeout(processingTimeoutRef.current);
    }
    processingTimeoutRef.current = setTimeout(() => {
      console.error('[SpeakingChatbotV2] Response timeout - clearing processing state');
      setIsProcessing(false);
      setStatusMessage('');
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Request timed out. This may happen when reading large documents. Please try again.' 
      }]);
    }, 300000);
    
    console.log('[SpeakingChatbotV2] Calling sendText now...');
    sendText(text);
    console.log('[SpeakingChatbotV2] sendText call completed');
  }, [sendText]);

  return {
    messages,
    isProcessing,
    statusMessage, // Export status message for UI display
    isStarting: isConnecting,
    isReady,
    isPlaying,
    isRecording,
    partialTranscript,
    audioLevel,
    metrics,
    availableDevices,
    selectedDeviceId,
    setSelectedDeviceId,
    isConversationMode,
    startChat,
    commitSpeech,
    stopChat,
    sendTextMessage,
  };
};
